import React from 'react';
import { Card, Typography } from 'antd';

const { Title, Text, Paragraph } = Typography;

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-page" style={{
      background: 'linear-gradient(135deg, #212169 0%, #2a2a7a 30%, #f8f9fa 30%, #f8f9fa 100%)',
      minHeight: '100vh',
      padding: '80px 16px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start'
    }}>
      <Card style={{
        width: "100%",
        maxWidth: 900,
        padding: 40,
        borderRadius: 16,
        background: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(33, 33, 105, 0.1)',
        boxShadow: '0 8px 32px rgba(33, 33, 105, 0.15)',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <Title
            level={1}
            style={{
              marginBottom: 8,
              fontWeight: 700,
              color: '#212169',
              fontSize: '2.5rem'
            }}
          >
            Hooptie.io — Privacy Policy
          </Title>
          <Text
            style={{
              color: 'rgba(33, 33, 105, 0.7)',
              fontSize: 14,
              lineHeight: 1.6
            }}
          >
            Last Updated: December 9, 2025
          </Text>
        </div>

        <div style={{ color: '#212169', lineHeight: 1.8 }}>
          <Paragraph style={{ fontSize: 16, marginBottom: 24 }}>
            Hooptie ("Hooptie.io," "we," "our," or "us") provides automotive market insights, analytics tools, and AI-powered recommendations for independent dealerships. This Privacy Policy explains how we collect, use, protect, and share your information when you use our website, platform, and services (the "Services").
          </Paragraph>

          <Paragraph style={{ fontSize: 16, marginBottom: 32 }}>
            By accessing or using Hooptie.io, you agree to the terms of this Privacy Policy.
          </Paragraph>

          <Title level={2} style={{ color: '#212169', marginTop: 32, marginBottom: 16, fontSize: '1.5rem' }}>
            1. Information We Collect
          </Title>

          <Title level={3} style={{ color: '#212169', marginTop: 24, marginBottom: 12, fontSize: '1.25rem' }}>
            1.1 Information You Provide
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 16 }}>
            We may collect the following information when you sign up or use the platform:
          </Paragraph>
          <ul style={{ fontSize: 15, marginBottom: 24, paddingLeft: 24 }}>
            <li style={{ marginBottom: 8 }}>
              <strong>Account information:</strong> Name, email address, dealership name, phone number, and password.
            </li>
            <li style={{ marginBottom: 8 }}>
              <strong>Billing information:</strong> Payment details processed securely by Stripe (we do not store full card numbers on our servers).
            </li>
            <li style={{ marginBottom: 8 }}>
              <strong>Dealership data:</strong> Inventory information you upload or connect manually, including VINs, prices, mileage, and notes.
            </li>
            <li style={{ marginBottom: 8 }}>
              <strong>Communications:</strong> Messages you send to us, support requests, and feedback.
            </li>
          </ul>

          <Title level={3} style={{ color: '#212169', marginTop: 24, marginBottom: 12, fontSize: '1.25rem' }}>
            1.2 Automatically Collected Information
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 16 }}>
            When you use Hooptie.io, we automatically collect:
          </Paragraph>
          <ul style={{ fontSize: 15, marginBottom: 24, paddingLeft: 24 }}>
            <li style={{ marginBottom: 8 }}>
              <strong>Usage data:</strong> Pages visited, buttons clicked, time spent on different parts of the platform.
            </li>
            <li style={{ marginBottom: 8 }}>
              <strong>Device & technical data:</strong> Browser type, IP address, operating system, referral URLs.
            </li>
            <li style={{ marginBottom: 8 }}>
              <strong>Analytics data:</strong> Session replays, heatmaps, and usage metrics via tools like PostHog or similar analytics providers.
            </li>
            <li style={{ marginBottom: 8 }}>
              <strong>Cookies:</strong> Used for authentication, personalization, and improving platform performance.
            </li>
          </ul>

          <Title level={3} style={{ color: '#212169', marginTop: 24, marginBottom: 12, fontSize: '1.25rem' }}>
            1.3 Third-Party Data
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 24 }}>
            We may receive data from third-party APIs, such as:
          </Paragraph>
          <ul style={{ fontSize: 15, marginBottom: 32, paddingLeft: 24 }}>
            <li style={{ marginBottom: 8 }}>Market listings and sold vehicle data</li>
            <li style={{ marginBottom: 8 }}>Vehicle history providers</li>
            <li style={{ marginBottom: 8 }}>VIN decoding services</li>
          </ul>
          <Paragraph style={{ fontSize: 15, marginBottom: 32 }}>
            This information helps power our insights, pricing models, and AI recommendations.
          </Paragraph>

          <Title level={2} style={{ color: '#212169', marginTop: 32, marginBottom: 16, fontSize: '1.5rem' }}>
            2. How We Use Your Information
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 16 }}>
            We use the information we collect to:
          </Paragraph>
          <ul style={{ fontSize: 15, marginBottom: 24, paddingLeft: 24 }}>
            <li style={{ marginBottom: 8 }}>Provide, maintain, and improve the Hooptie platform</li>
            <li style={{ marginBottom: 8 }}>Generate AI-powered insights and recommendations</li>
            <li style={{ marginBottom: 8 }}>Analyze dealership market conditions and inventory performance</li>
            <li style={{ marginBottom: 8 }}>Customize your user experience</li>
            <li style={{ marginBottom: 8 }}>Communicate updates, product announcements, and support responses</li>
            <li style={{ marginBottom: 8 }}>Process payments and manage billing</li>
            <li style={{ marginBottom: 8 }}>Prevent fraud, misuse, or unauthorized access</li>
          </ul>
          <Paragraph style={{ fontSize: 15, marginBottom: 32, fontWeight: 500 }}>
            We do not sell your personal information to third parties.
          </Paragraph>

          <Title level={2} style={{ color: '#212169', marginTop: 32, marginBottom: 16, fontSize: '1.5rem' }}>
            3. How We Share Information
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 24 }}>
            We may share your information in the following situations:
          </Paragraph>

          <Title level={3} style={{ color: '#212169', marginTop: 24, marginBottom: 12, fontSize: '1.25rem' }}>
            3.1 Service Providers
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 16 }}>
            We share data with trusted vendors who help operate the platform, including:
          </Paragraph>
          <ul style={{ fontSize: 15, marginBottom: 24, paddingLeft: 24 }}>
            <li style={{ marginBottom: 8 }}>Stripe (billing and payments)</li>
            <li style={{ marginBottom: 8 }}>Analytics providers (PostHog, etc.)</li>
            <li style={{ marginBottom: 8 }}>Hosting providers (Heroku, AWS, or similar)</li>
            <li style={{ marginBottom: 8 }}>AI processing services (OpenAI or similar)</li>
          </ul>
          <Paragraph style={{ fontSize: 15, marginBottom: 24 }}>
            These companies only access information as needed to perform their functions.
          </Paragraph>

          <Title level={3} style={{ color: '#212169', marginTop: 24, marginBottom: 12, fontSize: '1.25rem' }}>
            3.2 Legal Requirements
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 24 }}>
            We may disclose information if required by law, government request, or to protect our rights.
          </Paragraph>

          <Title level={3} style={{ color: '#212169', marginTop: 24, marginBottom: 12, fontSize: '1.25rem' }}>
            3.3 Business Transfers
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 32 }}>
            If Hooptie is involved in a merger, acquisition, or asset sale, user information may be transferred as part of the transaction.
          </Paragraph>

          <Title level={2} style={{ color: '#212169', marginTop: 32, marginBottom: 16, fontSize: '1.5rem' }}>
            4. Data Security
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 32 }}>
            We implement reasonable security measures—including encryption, access controls, and regular monitoring—to protect your information.
          </Paragraph>
          <Paragraph style={{ fontSize: 15, marginBottom: 32, fontStyle: 'italic' }}>
            However, no online service can guarantee absolute security.
          </Paragraph>

          <Title level={2} style={{ color: '#212169', marginTop: 32, marginBottom: 16, fontSize: '1.5rem' }}>
            5. Your Choices & Rights
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 16 }}>
            Depending on your location, you may have rights to:
          </Paragraph>
          <ul style={{ fontSize: 15, marginBottom: 24, paddingLeft: 24 }}>
            <li style={{ marginBottom: 8 }}>Access and download your data</li>
            <li style={{ marginBottom: 8 }}>Update or correct your information</li>
            <li style={{ marginBottom: 8 }}>Request deletion of your account</li>
            <li style={{ marginBottom: 8 }}>Opt out of marketing communications</li>
            <li style={{ marginBottom: 8 }}>Restrict certain processing</li>
          </ul>
          <Paragraph style={{ fontSize: 15, marginBottom: 32 }}>
            You can manage most settings in your account or by contacting us at{' '}
            <a href="mailto:support@hooptie.io" style={{ color: '#212169', textDecoration: 'underline' }}>
              support@hooptie.io
            </a>
            .
          </Paragraph>

          <Title level={2} style={{ color: '#212169', marginTop: 32, marginBottom: 16, fontSize: '1.5rem' }}>
            6. Data Retention
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 16 }}>
            We retain your information as long as your account is active or needed to provide the Services.
          </Paragraph>
          <Paragraph style={{ fontSize: 15, marginBottom: 32 }}>
            If you request deletion, we will remove your data unless required to keep it for legal or billing purposes.
          </Paragraph>

          <Title level={2} style={{ color: '#212169', marginTop: 32, marginBottom: 16, fontSize: '1.5rem' }}>
            7. Children's Privacy
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 32 }}>
            Hooptie is not intended for individuals under 18, and we do not knowingly collect information from minors.
          </Paragraph>

          <Title level={2} style={{ color: '#212169', marginTop: 32, marginBottom: 16, fontSize: '1.5rem' }}>
            8. International Users
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 32 }}>
            If you access Hooptie from outside the United States, your data may be transferred to and processed in the U.S., where privacy laws may differ.
          </Paragraph>

          <Title level={2} style={{ color: '#212169', marginTop: 32, marginBottom: 16, fontSize: '1.5rem' }}>
            9. Changes to This Privacy Policy
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 32 }}>
            We may update this Privacy Policy from time to time. If changes are significant, we will notify you via email or platform notice.
          </Paragraph>

          <Title level={2} style={{ color: '#212169', marginTop: 32, marginBottom: 16, fontSize: '1.5rem' }}>
            10. Contact Us
          </Title>
          <Paragraph style={{ fontSize: 15, marginBottom: 16 }}>
            If you have questions about this Privacy Policy or how your data is handled, please contact us:
          </Paragraph>
          <div style={{ fontSize: 15, marginBottom: 32, paddingLeft: 8 }}>
            <Paragraph style={{ marginBottom: 4, fontWeight: 500 }}>Hooptie, LLC</Paragraph>
            <Paragraph style={{ marginBottom: 4 }}>
              Email:{' '}
              <a href="mailto:support@hooptie.io" style={{ color: '#212169', textDecoration: 'underline' }}>
                support@hooptie.io
              </a>
            </Paragraph>
            <Paragraph style={{ marginBottom: 4 }}>
              Website:{' '}
              <a href="https://hooptie.io" target="_blank" rel="noopener noreferrer" style={{ color: '#212169', textDecoration: 'underline' }}>
                https://hooptie.io
              </a>
            </Paragraph>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PrivacyPolicy;



